import { useEffect, useState } from 'react';
import { absenceApi } from '../../../api/absence.js';
import { hrApi } from '../../../api/hr.js';

/**
 * Absence requests — a request form with a LIVE balance check (selecting an
 * employee + plan shows the current balance and warns when the requested amount
 * exceeds it before submit), plus an approve/reject queue of pending requests.
 */
export default function RequestsPage() {
  const [employees, setEmployees] = useState([]);
  const [plans, setPlans] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [planId, setPlanId] = useState('');
  const [hours, setHours] = useState('');
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  function loadQueue() {
    absenceApi
      .listRequests({ status: 'pending' })
      .then((r) => setQueue(r?.items || []))
      .catch(() => {}); // managers only — silent for self-service employees
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([
      hrApi.listEmployees().catch(() => ({ items: [] })),
      absenceApi.listPlans({ active: true }),
    ])
      .then(([e, p]) => {
        setEmployees(e?.items || e || []);
        setPlans(p?.items || []);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
    loadQueue();
  }, []);

  async function onSelectEmployee(id) {
    setEmployeeId(id);
    setNotice(null);
    setBalances([]);
    setRequests([]);
    if (!id) return;
    try {
      const [b, r] = await Promise.all([
        absenceApi.balances(id),
        absenceApi.listRequests({ employee: id }),
      ]);
      setBalances(b?.items || []);
      setRequests(r?.items || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load balances');
    }
  }

  const selectedBalance = balances.find((b) => b.planId === planId);
  const need = Number(hours || 0);
  const insufficient = selectedBalance && need > 0 && need > Number(selectedBalance.balance);

  async function createRequest(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    if (!employeeId) {
      setError('Select an employee first');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await absenceApi.createRequest({
        employeeId,
        absencePlanId: fd.get('absencePlanId'),
        startDate: fd.get('startDate'),
        endDate: fd.get('endDate'),
        hours: Number(fd.get('hours')),
        reason: fd.get('reason') || null,
      });
      setNotice('Absence requested');
      setHours('');
      onSelectEmployee(employeeId);
      loadQueue();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create request');
    } finally {
      setBusy(false);
    }
  }

  async function decide(id, action) {
    setBusy(true);
    try {
      if (action === 'approve') await absenceApi.approveRequest(id, {});
      else await absenceApi.rejectRequest(id, {});
      loadQueue();
      if (employeeId) onSelectEmployee(employeeId);
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${action}`);
    } finally {
      setBusy(false);
    }
  }

  async function cancel(id) {
    setBusy(true);
    try {
      await absenceApi.cancelRequest(id);
      if (employeeId) onSelectEmployee(employeeId);
      loadQueue();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section data-testid="absence-requests-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Absence Requests</h1>
          <p className="muted">Request time off (live balance check) + approval queue</p>
        </div>
      </header>

      {error && <div className="alert error" data-testid="absence-requests-error">{error}</div>}
      {notice && <div className="alert info" data-testid="absence-requests-notice">{notice}</div>}

      <form onSubmit={createRequest} className="card form-card grid-form">
        <label>Employee
          <select data-testid="absence-request-employee-input" value={employeeId} onChange={(e) => onSelectEmployee(e.target.value)}>
            <option value="">— select —</option>
            {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.user_name || emp.employee_code || emp.id}</option>)}
          </select>
        </label>
        <label>Plan
          <select name="absencePlanId" data-testid="absence-request-plan-input" required value={planId} onChange={(e) => setPlanId(e.target.value)}>
            <option value="" disabled>— select plan —</option>
            {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </label>
        <label>Start date<input name="startDate" type="date" data-testid="absence-request-start-input" required /></label>
        <label>End date<input name="endDate" type="date" data-testid="absence-request-end-input" required /></label>
        <label>Amount<input name="hours" type="number" min="0" step="0.25" value={hours} onChange={(e) => setHours(e.target.value)} data-testid="absence-request-hours-input" required /></label>
        <label>Reason<input name="reason" data-testid="absence-request-reason-input" /></label>
        <div className="full">
          {selectedBalance && (
            <p className={insufficient ? 'alert error' : 'muted'} data-testid="absence-request-balance-check">
              Current balance: {Number(selectedBalance.balance)} {selectedBalance.unit}
              {insufficient ? ` — insufficient for ${need}` : ''}
            </p>
          )}
        </div>
        <div className="full actions">
          <button className="btn primary" type="submit" data-testid="absence-request-submit" disabled={busy || insufficient}>Submit request</button>
        </div>
      </form>

      <div className="card">
        <h3>Requests</h3>
        <table className="data-table" data-testid="absence-request-list">
          <thead><tr><th>Plan</th><th>Dates</th><th>Amount</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {requests.map((r) => (
              <tr key={r.id} data-testid={`absence-request-row-${r.id}`}>
                <td>{r.plan_name}</td>
                <td>{String(r.start_date).slice(0, 10)} → {String(r.end_date).slice(0, 10)}</td>
                <td>{Number(r.hours)} {r.plan_unit}</td>
                <td data-testid={`absence-request-status-${r.id}`}>{r.status}</td>
                <td>
                  {(r.status === 'pending' || r.status === 'approved') && (
                    <button className="btn ghost" data-testid={`absence-request-cancel-${r.id}`} onClick={() => cancel(r.id)} disabled={busy}>Cancel</button>
                  )}
                </td>
              </tr>
            ))}
            {!loading && employeeId && requests.length === 0 && <tr><td colSpan={5} className="muted">No requests yet.</td></tr>}
            {!employeeId && <tr><td colSpan={5} className="muted">Select an employee to see requests.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h3>Approval queue</h3>
        <table className="data-table" data-testid="absence-approval-list">
          <thead><tr><th>Employee</th><th>Plan</th><th>Dates</th><th>Amount</th><th>Actions</th></tr></thead>
          <tbody>
            {queue.map((r) => (
              <tr key={r.id} data-testid={`absence-approval-row-${r.id}`}>
                <td>{r.employee_name || r.employee_code || r.employee_id}</td>
                <td>{r.plan_name}</td>
                <td>{String(r.start_date).slice(0, 10)} → {String(r.end_date).slice(0, 10)}</td>
                <td>{Number(r.hours)} {r.plan_unit}</td>
                <td>
                  <button className="btn primary" data-testid={`absence-approve-${r.id}`} onClick={() => decide(r.id, 'approve')} disabled={busy}>Approve</button>
                  <button className="btn ghost" data-testid={`absence-reject-${r.id}`} onClick={() => decide(r.id, 'reject')} disabled={busy}>Reject</button>
                </td>
              </tr>
            ))}
            {queue.length === 0 && <tr><td colSpan={5} className="muted">Nothing awaiting approval.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}
